import { useOrdersConfig } from '../config';
import { ALL, FILTER_KEYS, SEARCH_DEBOUNCE_TIME, STATUS_OPTIONS, TEXT_FILTERS } from '../consts';
import useFilters, { areFiltersModified } from '../hooks/useFilters';
import { formatDate } from '../utils';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import SingleSelect from 'shared/BIDS/components/SingleSelect';
import DateRangeInput from 'shared/components/DateRangeInput';
import TextInput from 'shared/components/TextInput';
import useMobileLayout from 'shared/hooks/useMobileLayout';
import usePageResettingFilters from 'shared/hooks/usePageResettingFilters';
import { DateTypeArg } from 'shared/types/dates';

function Filters() {
  const isMobileLayout = useMobileLayout();
  const { applyFilters, clearFilters } = usePageResettingFilters(FILTER_KEYS.pageNum);

  const { deliveryMethodOptions, paymentTypeOptions } = useOrdersConfig();
  const filterParams = useFilters();
  const { status, deliveryMethod, paymentType, clientName, orderRef, createdDateFrom, createdDateTo } = filterParams;

  const [selectedTextFilter, setSelectedTextFilter] = useState(() =>
    clientName ? FILTER_KEYS.clientName : FILTER_KEYS.orderRef
  );

  const debouncedApplyFilters = useMemo(() => debounce(applyFilters, SEARCH_DEBOUNCE_TIME), [applyFilters]);

  // A pending call closes over a stale `applyFilters`, so drop it whenever that identity changes or we unmount.
  useEffect(() => () => debouncedApplyFilters.cancel(), [debouncedApplyFilters]);

  // Deep links and back/forward navigation change the URL without remounting; follow whichever text filter it carries.
  useEffect(() => {
    if (clientName) {
      setSelectedTextFilter(FILTER_KEYS.clientName);
    } else if (orderRef) {
      setSelectedTextFilter(FILTER_KEYS.orderRef);
    }
  }, [clientName, orderRef]);

  const selectTextFilter = useCallback(
    (filterKey: string) => {
      debouncedApplyFilters.cancel();
      setSelectedTextFilter(filterKey);
    },
    [debouncedApplyFilters]
  );

  const clearTextFilter = useCallback(
    (filterKey: string) => {
      debouncedApplyFilters.cancel();
      clearFilters(filterKey);
    },
    [clearFilters, debouncedApplyFilters]
  );

  const resetCallback = useCallback(() => {
    debouncedApplyFilters.cancel();
    clearFilters(
      FILTER_KEYS.status,
      FILTER_KEYS.deliveryMethod,
      FILTER_KEYS.paymentType,
      FILTER_KEYS.clientName,
      FILTER_KEYS.orderRef,
      FILTER_KEYS.createdDateFrom,
      FILTER_KEYS.createdDateTo
    );
  }, [clearFilters, debouncedApplyFilters]);

  const filtersModified = areFiltersModified(filterParams);

  return (
    <div className="flex flex-col gap-4 pb-4">
      <div className={`flex items-center gap-4 ${isMobileLayout ? 'overflow-scroll no-scrollbar' : 'flex-wrap'}`}>
        <button
          disabled={!filtersModified}
          className="tracking-tight text-sm underline disabled:text-palette-light-grey text-palette-gold"
          onClick={resetCallback}
        >
          Reset
        </button>
        <div className="border-r border-palette-color-bg-grey w-px h-8" />
        <SingleSelect
          prefix="Status"
          options={STATUS_OPTIONS}
          value={status}
          onChange={(v: string) => applyFilters({ [FILTER_KEYS.status]: v })}
        />
        <SingleSelect
          prefix="Delivery"
          options={deliveryMethodOptions}
          value={deliveryMethod ?? ALL}
          onChange={(v: string) => applyFilters({ [FILTER_KEYS.deliveryMethod]: v === ALL ? '' : v })}
        />
        <SingleSelect
          prefix="Payment"
          options={paymentTypeOptions}
          value={paymentType ?? ALL}
          onChange={(v: string) => applyFilters({ [FILTER_KEYS.paymentType]: v === ALL ? '' : v })}
        />
        <DateRangeInput
          fromDate={createdDateFrom || null}
          toDate={createdDateTo || null}
          onChange={(dates: DateTypeArg) => {
            const [from, to] = dates;
            applyFilters({
              [FILTER_KEYS.createdDateFrom]: from ? formatDate(from) : '',
              [FILTER_KEYS.createdDateTo]: to ? formatDate(to) : '',
            });
          }}
          onClear={() => applyFilters({ [FILTER_KEYS.createdDateFrom]: '', [FILTER_KEYS.createdDateTo]: '' })}
        />
      </div>
      <div className="flex flex-col gap-4 w-full sm:max-w-sm">
        <div className="flex items-center gap-4">
          {TEXT_FILTERS.map((option) => (
            <div key={option.id} className="flex gap-1.5 items-center">
              <input
                type="radio"
                id={option.id}
                name="dgc-text-filter"
                value={option.id}
                checked={selectedTextFilter === option.id}
                onChange={() => selectTextFilter(option.id)}
                className="text-palette-burgundy focus:ring-palette-burgundy checked:bg-none"
              />
              <label htmlFor={option.id} className="font-semibold text-sm text-nowrap whitespace-nowrap">
                {option.label}
              </label>
            </div>
          ))}
        </div>
        {selectedTextFilter === FILTER_KEYS.orderRef ? (
          <TextInput
            initValue={orderRef}
            placeholder="Search Order Ref"
            onUpdate={(value) => debouncedApplyFilters({ [FILTER_KEYS.clientName]: '', [FILTER_KEYS.orderRef]: value })}
            onClear={() => clearTextFilter(FILTER_KEYS.orderRef)}
          />
        ) : selectedTextFilter === FILTER_KEYS.clientName ? (
          <TextInput
            initValue={clientName}
            placeholder="Search Client Name"
            onUpdate={(value) => debouncedApplyFilters({ [FILTER_KEYS.orderRef]: '', [FILTER_KEYS.clientName]: value })}
            onClear={() => clearTextFilter(FILTER_KEYS.clientName)}
          />
        ) : null}
      </div>
    </div>
  );
}

export default Filters;
